import { CircularProgress, Stack, Typography } from "@mui/material";
import {
  type ProfileLookup,
  usePublicProfileQuery,
} from "Api/Queries/Social/usePublicProfileQuery";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { ProfileContent } from "Pages/PublicProfile/ProfileContent";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.PublicProfile;

type Props = {
  /** Validated handle or persona PublicID (the hosting route checks the shape). */
  lookup: ProfileLookup;
};

/**
 * The profile paper shared by the anonymous and the in-app variant of /profile/{key}:
 * loading spinner, the profile itself, or the unknown-key/error state.
 */
export const ProfilePaper = ({ lookup }: Props) => {
  const { t } = useResource();
  const { data, isLoading, error } = usePublicProfileQuery(lookup);

  const isNotFound =
    error !== null &&
    typeof error === "object" &&
    "status" in error &&
    error.status === 404;

  return (
    <PrimaryPaper
      sx={{ p: 5, textAlign: "center", maxWidth: 640, width: "100%" }}
    >
      {isLoading ? (
        <CircularProgress />
      ) : data ? (
        <ProfileContent profile={data} />
      ) : (
        <Stack gap={1.5}>
          {"handle" in lookup && (
            <Typography variant="h5" fontWeight={500}>
              @{lookup.handle}
            </Typography>
          )}
          <Typography color="text.secondary">
            {t(
              isNotFound
                ? RESOURCES.NotFound
                : Resources.Errors.DataLoadingError,
            )}
          </Typography>
        </Stack>
      )}
    </PrimaryPaper>
  );
};
